<template lang="pug">
.briq-form-element
  label Behavior*
  div
    ProductSchedulingOptions(
      :disabled="disabled"
      :selectorAvailability="selectorAvailability"
      :isParallelProduct="isParallelProduct"
      :schedulingBehavior="schedulingBehavior"
      :isSequential="isSequential"
      @selectSequential="selectSequential"
      @selectSchedulingBehavior="selectSchedulingBehavior"
      )
  .product-scheduling-placeholder.sequential-fields(v-if="isSequential && !this.isParallelProduct")
    ProductSchedulingSequentialConfig(
      :disabled="disabled"
      :enableProductDurationPerParticipant="enableProductDurationPerParticipant"
      :vuelidate="vuelidate"
      :maximumDurationError="maximumDurationError"
      :startDurationError="startDurationError"
      :selectorAvailability="selectorAvailability"
      :schedulingBehavior="schedulingBehavior"
      :isSequential="isSequential"
      :product="product"
      :resource="resource"
      @selectSchedulingBehavior="selectSchedulingBehavior"
      )
  .product-scheduling-placeholder.sequential-fields(v-if="this.isParallelProduct")
    ProductSchedulingParallelConfig(
      :disabled="disabled"
      :product="product"
      :maximumDurationError="maximumDurationError"
      :startDurationError="startDurationError"
      )
</template>

<script>
import { isNil } from 'ramda'
import BriqIsoDurationInput from '@v/components/BriqIsoDurationInput.vue'
import ProductSchedulingOptions from './ProductSchedulingConfig/ProductSchedulingOptions.vue'
import ProductSchedulingSequentialConfig from './ProductSchedulingConfig/ProductSchedulingSequentialConfig.vue'
import ProductSchedulingParallelConfig from './ProductSchedulingConfig/ProductSchedulingParallelConfig.vue'
import getSchedulingBehavior from './getSchedulingBehavior.js'
import DurationType from '@v/inventory/products/edit/models/DurationType.js'

export default {
  name: 'ProductSchedulingConfig',
  components: {
    BriqIsoDurationInput,
    ProductSchedulingOptions,
    ProductSchedulingSequentialConfig,
    ProductSchedulingParallelConfig,
  },
  props: {
    vuelidate: Object,
    product: Object,
    resource: Object,
    disabled: Boolean,
    enableProductDurationPerParticipant: Boolean,
  },
  data() {
    return {
      schedulingBehavior: '',
      selectorAvailability: {
        sequential: true,
        parallel: true,
        noDuration: true,
        fixedDuration: true,
        perParticipants: true,
      },
      zeroDuration: 'PT0S',
      originalModifiedDuration: null,
    }
  },
  computed: {
    isParallelProduct() {
      return this.schedulingBehavior === 'parallel'
    },
    isSequential() {
      return (
        this.schedulingBehavior === 'fixedDuration' || this.schedulingBehavior === 'perParticipants'
      )
    },
    maximumDurationError() {
      if (!this.vuelidate.product.ProductDurationConfig?.MaximumDuration?.$error) return null
      return 'Maximum duration needs to be equal or greater than the minimum duration.'
    },
    startDurationError() {
      if (!this.vuelidate.product.ProductDurationConfig?.StartDuration?.$error) return null
      return 'Start duration needs to be equal or less then the maximum duration'
    },
  },
  methods: {
    selectSequential() {
      // "sequential" is not a final scheduling behavior, so check if we need to set the default, which is "fixedDuration"
      if (!this.isSequential) {
        this.schedulingBehavior = 'fixedDuration'
        this.updateSchedulingBehavior(this.schedulingBehavior)
      }
    },
    selectSchedulingBehavior(behavior) {
      this.schedulingBehavior = behavior
      this.updateSchedulingBehavior(this.schedulingBehavior)
    },
    selectorIsDisabled(key) {
      return this.disabled || this.selectorAvailability?.[key] === false || this.isParallelProduct
    },
    setSchedulerType() {
      this.schedulingBehavior =
        this.product.Key === null
          ? getSchedulingBehavior(this.resource)
          : this.getDurationStateFromProduct()

      this.updateSchedulingBehavior(this.schedulingBehavior)
      if (!isNil(this.resource?.SupportedProductDurationTypes)) {
        this.selectorAvailability = {
          sequential:
            this.resource.SupportedProductDurationTypes.includes(DurationType.FixedDuration) ||
            this.resource.SupportedProductDurationTypes.includes(DurationType.PerParticipant),
          fixedDuration: this.resource.SupportedProductDurationTypes.includes(
            DurationType.FixedDuration,
          ),
          perParticipants: this.resource.SupportedProductDurationTypes.includes(
            DurationType.PerParticipant,
          ),
          noDuration: this.resource.SupportedProductDurationTypes.includes(DurationType.NoDuration),
          parallel: this.resource.SupportedProductDurationTypes.includes(
            DurationType.ParallelScheduling,
          ),
        }
      }
    },
    getDurationStateFromProduct() {
      switch (this.product.ProductDurationConfig.DurationType) {
        case DurationType.FixedDuration:
          return 'fixedDuration'
        case DurationType.PerParticipant:
          return 'perParticipants'
        case DurationType.ParallelScheduling:
          return 'parallel'
        case DurationType.NoDuration:
          return 'noDuration'
      }
    },
    updateSchedulingBehavior(newVal) {
      this.updateDurationType(newVal)

      const getModifiedDuration = schedulingBehavior =>
        schedulingBehavior === 'noDuration'
          ? null
          : this.product.ProductDurationConfig.CustomDuration !== this.zeroDuration
          ? this.originalModifiedDuration
          : null
      this.product.ProductDurationConfig.CustomDuration = getModifiedDuration(newVal)
      const isPerParticipants = newVal === 'perParticipants'
      const isParallel = newVal === 'parallel'
      if (!isPerParticipants) {
        this.product.ProductDurationConfig.StartDuration = null
        this.product.ProductDurationConfig.NumberOfParticipantsPerDuration = null
      }

      if (!isPerParticipants && !isParallel) {
        this.product.ProductDurationConfig.MinimumDuration = null
        this.product.ProductDurationConfig.MaximumDuration = null
      }

      if (!isParallel) {
        this.product.ProductDurationConfig.ParallelExtraTimeAtEnd = null
        this.product.ProductDurationConfig.ParallelExtraTimeBeforeStart = null
      }

      this.$emit('schedulingBehaviorSelected', newVal)
    },
    updateDurationType(val) {
      const getDurationType = durationType => {
        switch (durationType) {
          case 'sequential':
          case 'fixedDuration':
            return DurationType.FixedDuration
          case 'perParticipants':
            return DurationType.PerParticipant
          case 'parallel':
            return DurationType.ParallelScheduling
          case 'noDuration':
            return DurationType.NoDuration
          default:
            return DurationType.FixedDuration
        }
      }
      this.product.ProductDurationConfig.DurationType = getDurationType(val)
    },
  },
  watch: {
    resource() {
      this.setSchedulerType()
    },
  },
  created() {
    this.originalModifiedDuration = this.product.ProductDurationConfig.CustomDuration
  },
  mounted() {
    this.setSchedulerType()
  },
}
</script>

<style scoped lang="stylus">
@import '~@v/variables.styl'

.product-scheduling-placeholder
  background-color grey200
  border-radius 5px
  padding 15px
  margin-top 10px
</style>
